'use client';

import { useState } from 'react';
import { Check, Copy, Facebook, Linkedin, Mail, Twitter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ShareType } from '@kit/community';
import { trackPostShareAction } from '@kit/community/server/actions';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

interface ShareDialogProps {
  postId: string;
  postUrl: string;
  postBody: string | null;
  isOpen: boolean;
  onClose: () => void;
}

// WhatsApp icon component (not in lucide-react)
function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export function ShareDialog({
  postId,
  postUrl,
  postBody,
  isOpen,
  onClose,
}: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const body = postBody ?? '';
  // Truncate by CODE POINTS, not UTF-16 units — String.slice can split an
  // emoji's surrogate pair and the lone surrogate makes encodeURIComponent
  // throw "URI malformed" (crashed the whole card on emoji-heavy posts).
  const bodyPoints = Array.from(body);
  const shareText =
    bodyPoints.slice(0, 100).join('') + (bodyPoints.length > 100 ? '...' : '');
  const encodedUrl = encodeURIComponent(postUrl);
  const encodedText = encodeURIComponent(shareText);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      toast.success(t('community:share.linkCopied'));

      // Track the share
      await trackPostShareAction(postId, 'copy_link');

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('community:share.copyFailed'));
    }
  };

  const handleSocialShare = async (platform: ShareType, url: string) => {
    // Track the share
    await trackPostShareAction(postId, platform);

    // Open share URL in new window
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    email: `mailto:?subject=${encodeURIComponent(t('community:share.emailSubject'))}&body=${encodedText}%0A%0A${encodedUrl}`,
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans i18nKey="community:share.title" defaults="Share Post" />
          </DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey="community:share.description"
              defaults="Share this post with your network"
            />
          </DialogDescription>
        </DialogHeader>

        {/* `min-w-0` on this grid item is load-bearing: DialogContent is a CSS
            grid whose default track is min-content. Without it the unbroken URL
            string sets a min-width wider than the panel's max-w, spilling the
            Copy button and the right column of the share grid outside the
            panel. */}
        <div className="min-w-0 space-y-4">
          {/* Copy Link */}
          <div className="flex min-w-0 items-center gap-2">
            <div className="bg-muted min-w-0 flex-1 truncate rounded-md px-3 py-2 text-sm">
              {postUrl}
            </div>
            <Button
              type="button"
              size="sm"
              variant={copied ? 'default' : 'outline'}
              onClick={handleCopyLink}
              className="shrink-0"
            >
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  <Trans i18nKey="community:share.copied" defaults="Copied" />
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  <Trans i18nKey="community:share.copy" defaults="Copy" />
                </>
              )}
            </Button>
          </div>

          {/* Social Share Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSocialShare('twitter', shareLinks.twitter)}
            >
              <Twitter className="mr-2 h-4 w-4" />
              {/* i18n-ignore: social network brand name */}
              Twitter
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSocialShare('facebook', shareLinks.facebook)}
            >
              <Facebook className="mr-2 h-4 w-4" />
              {/* i18n-ignore: social network brand name */}
              Facebook
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSocialShare('linkedin', shareLinks.linkedin)}
            >
              <Linkedin className="mr-2 h-4 w-4" />
              {/* i18n-ignore: social network brand name */}
              LinkedIn
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleSocialShare('whatsapp', shareLinks.whatsapp)}
            >
              <WhatsAppIcon className="mr-2 h-4 w-4" />
              {/* i18n-ignore: social network brand name */}
              WhatsApp
            </Button>
          </div>

          {/* Email Share */}
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => handleSocialShare('email', shareLinks.email)}
          >
            <Mail className="mr-2 h-4 w-4" />
            <Trans i18nKey="community:share.email" defaults="Share via Email" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
